// src/context/ThemeContext.jsx
import React, { createContext, useState, useContext, useMemo, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createAppTheme } from '../theme';

// Crear el contexto
const ThemeContext = createContext({
  mode: 'light',
  toggleTheme: () => {},
});

// Hook personalizado para usar el contexto
export const useThemeMode = () => useContext(ThemeContext);

// Proveedor del tema
export const ThemeProvider = ({ children }) => {
  // Intentar obtener el modo del tema desde localStorage o preferencia del sistema
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode === 'light' || savedMode === 'dark') {
      return savedMode;
    }
    // Forzar light como default, ignorando la preferencia del sistema
    const envDefault = import.meta.env.VITE_DEFAULT_THEME_MODE;
    return envDefault === 'dark' ? 'dark' : 'light';
  });   
    
  // Función para alternar el tema
  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // Guardar el modo en localStorage cuando cambie
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  // Escuchar cambios en la preferencia del sistema
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      // Solo cambiar automáticamente si el usuario no ha establecido una preferencia
      if (!localStorage.getItem('themeMode')) {
        setMode(mediaQuery.matches ? 'dark' : 'light');
      }
    };
    
    // Algunos navegadores antiguos usan addListener/removeListener en lugar de addEventListener/removeEventListener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Crear el tema basado en el modo actual
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  // Valores del contexto
  const contextValue = useMemo(() => ({
    mode,
    toggleTheme,
  }), [mode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
